// Bahnstrecke für „Bahn statt Lkw“: kommt von außen aus dem Bahnnetz über die Hügel
// bis an das Containerlager des Logistikzentrums. Sie verläuft in der Lücke zwischen
// den Zufahrten zu Recyclinghof und Wohngebiet und kreuzt daher keine Straße.
import * as THREE from "three"
import gsap from "gsap"
import { COLORS } from "./palette.js"
import { at, box, mat } from "./builders.js"
import { heightAt } from "./terrain.js"

const START = new THREE.Vector3(17, 0, 26)
const DIRECTION = new THREE.Vector3(Math.cos(THREE.MathUtils.degToRad(78)), 0, Math.sin(THREE.MathUtils.degToRad(78)))
const LENGTH = 150
const SLEEPER_GAP = 1
const CAR_SPACING = 2.9

export function railPoint(distance) {
  const p = START.clone().addScaledVector(DIRECTION, distance)
  p.y = heightAt(p.x, p.z) + 0.05
  return p
}

export function distanceToRail(x, z) {
  const dx = x - START.x
  const dz = z - START.z
  const along = THREE.MathUtils.clamp(dx * DIRECTION.x + dz * DIRECTION.z, 0, LENGTH)
  return Math.hypot(dx - DIRECTION.x * along, dz - DIRECTION.z * along)
}

// Schneise im Wald entlang der Strecke
export function railKeepOut() {
  const circles = []
  for (let d = 0; d <= LENGTH; d += 4) {
    const p = START.clone().addScaledVector(DIRECTION, d)
    circles.push({ x: p.x, z: p.z, r: 3.2 })
  }
  return circles
}

function carModel(index) {
  const g = new THREE.Group()
  if (index === 0) {
    g.add(at(box(2.6, 1.3, 1.2, COLORS.red), 0, 0.25, 0))
    g.add(at(box(0.9, 0.5, 1.22, COLORS.glass), 0.6, 1.0, 0))
    g.add(at(box(0.3, 0.5, 0.3, COLORS.navy), -0.8, 1.55, 0))
  } else {
    g.add(at(box(2.5, 1.1, 1.1, index % 2 ? COLORS.green : COLORS.steel), 0, 0.25, 0))
  }
  g.add(at(box(2.2, 0.2, 0.9, COLORS.navy), 0, 0.05, 0))
  g.traverse((child) => {
    if (child.isMesh) child.castShadow = true
  })
  return g
}

// Baut die Strecke animiert auf und lässt danach den Zug pendeln.
export function buildRailway(world) {
  const count = Math.floor(LENGTH / SLEEPER_GAP)
  const sleepers = new THREE.InstancedMesh(new THREE.BoxGeometry(0.4, 0.1, 1.8), mat(0x7a5a3a), count)
  const rails = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 0.1, 0.1), mat(COLORS.steelDark), count * 2)
  sleepers.receiveShadow = true
  rails.receiveShadow = true
  // Die Strecke wächst während der Animation; die automatische Sichtbarkeitsprüfung
  // würde sonst mit dem leeren Anfangszustand rechnen und die Gleise wegschneiden.
  sleepers.frustumCulled = false
  rails.frustumCulled = false
  const matrix = new THREE.Matrix4()
  const quaternion = new THREE.Quaternion()
  const side = new THREE.Vector3(-DIRECTION.z, 0, DIRECTION.x)
  for (let i = 0; i < count; i++) {
    const a = railPoint(i * SLEEPER_GAP)
    const b = railPoint((i + 1) * SLEEPER_GAP)
    const direction = b.clone().sub(a)
    const length = direction.length()
    quaternion.setFromUnitVectors(new THREE.Vector3(1, 0, 0), direction.normalize())
    matrix.compose(a, quaternion, new THREE.Vector3(1, 1, 1))
    sleepers.setMatrixAt(i, matrix)
    const middle = a.clone().lerp(b, 0.5)
    for (const [k, offset] of [[0, 0.55], [1, -0.55]]) {
      matrix.compose(middle.clone().addScaledVector(side, offset).add(new THREE.Vector3(0, 0.08, 0)), quaternion, new THREE.Vector3(length, 1, 1))
      rails.setMatrixAt(i * 2 + k, matrix)
    }
  }
  sleepers.count = 0
  rails.count = 0
  world.dynamic.add(sleepers, rails)

  // Gleise wachsen vom Logistikzentrum hinaus ins Land
  const progress = { n: 0 }
  gsap.to(progress, {
    n: count, duration: 2.2, ease: "power1.in",
    onUpdate: () => {
      sleepers.count = Math.floor(progress.n)
      rails.count = Math.floor(progress.n) * 2
    }
  })

  // Zug: jeder Wagen läuft einzeln auf der Strecke und richtet sich nach ihr aus
  const cars = Array.from({ length: 5 }, (_, i) => carModel(i))
  cars.forEach((car) => {
    car.visible = false
    world.dynamic.add(car)
  })
  const minS = 3
  const maxS = LENGTH - CAR_SPACING * cars.length - 2
  const train = { s: maxS, dir: -1, pause: 0, started: false }
  gsap.delayedCall(2.4, () => {
    train.started = true
    cars.forEach((car) => (car.visible = true))
  })
  const ahead = new THREE.Vector3()
  world.extras.push({
    update(dt) {
      if (!train.started) return
      if (train.pause > 0) {
        train.pause -= dt
        return
      }
      train.s += train.dir * 7 * dt
      if (train.s <= minS || train.s >= maxS) {
        train.s = THREE.MathUtils.clamp(train.s, minS, maxS)
        train.dir *= -1
        train.pause = train.s <= minS ? 3 : 1
      }
      cars.forEach((car, i) => {
        // Lok vorne am Logistikzentrum, Wagen dahinter Richtung Land
        const s = train.s + i * CAR_SPACING
        car.position.copy(railPoint(s))
        ahead.copy(railPoint(s - 1))
        car.lookAt(ahead)
        car.rotateY(-Math.PI / 2)
      })
    }
  })
}
